import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Container, Label, Row } from "reactstrap";
import Select from "react-select";
import SweetAlert from "react-bootstrap-sweetalert";
import { CircularProgress } from "@material-ui/core";
import moment from "moment";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const endpoint = `${process.env.REACT_APP_TOKRI_API_URL}/api/home-screen-widgets`;
const statusOptions = [
  { value: "Y", label: "Enable" },
  { value: "N", label: "Disable" },
];
const aemTagOptions = [
  {
    value: "frasersexperience:FRx50+",
    label: "frasersexperience:FRx50+",
  },
];
const protectedWidgetName = (name = "") =>
  ["events", "store deals", "promotions", "from our blog"].some((value) =>
    name.toLowerCase().includes(value),
  );
const normalizeName = (name = "") => name.trim().toLowerCase();
const toDateInput = (value) =>
  value && value !== "null" ? moment(value).format("YYYY-MM-DDTHH:mm") : "";
const isEmptyDate = (value) => !value || value === "null";

const HomescreenWidgetForm = ({ props, edit }) => {
  const item = props?.location?.state?.item || {};
  const [name, setName] = useState("");
  const [sortOrder, setSortOrder] = useState(1);
  const [aemTag, setAemTag] = useState(aemTagOptions[0].value);
  const [status, setStatus] = useState("Y");
  const [publishedDate, setPublishedDate] = useState("");
  const [unpublishedDate, setUnpublishedDate] = useState("");
  const [neverEnding, setNeverEnding] = useState(false);
  const [token, setToken] = useState("");
  const [loader, setLoader] = useState(false);
  const [successAlert, setSuccessAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const authToken = localStorage.getItem("AuthenticationToken");
    setToken(authToken);
    if (edit) {
      setName(item.name || "");
      setSortOrder(item.sort_order || 1);
      setAemTag(item.tag || aemTagOptions[0].value);
      setStatus(item.status || "Y");
      setPublishedDate(toDateInput(item.published_date));
      setUnpublishedDate(toDateInput(item.unpublished_date));
      setNeverEnding(isEmptyDate(item.unpublished_date));
    } else {
      fetch(endpoint, { headers: { Authorization: `Bearer ${authToken}` } })
        .then((response) => response.json())
        .then((result) => {
          const widgets = Array.isArray(result) ? result : result?.data || [];
          setSortOrder(widgets.length + 1);
        })
        .catch(() => {});
    }
  }, [edit]);

  const submit = async () => {
    if (!name.trim())
      return setErrorMessage("Please enter name");
    if (!publishedDate) return setErrorMessage("Please select a published date.");
    if (!neverEnding && (!unpublishedDate || unpublishedDate < publishedDate))
      return setErrorMessage(
        unpublishedDate
          ? "Unpublished date should be after the published date."
          : "Please select an unpublished date.",
      );

    if (!edit && protectedWidgetName(name)) {
      try {
        const response = await fetch(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const result = await response.json();
        const widgets = Array.isArray(result) ? result : result?.data || [];
        const recordAlreadyExists = widgets.some(
          (widget) => normalizeName(widget.name) === normalizeName(name),
        );

        if (recordAlreadyExists) return setErrorMessage(`${name} Already Exist`);
      } catch (error) {
        console.log("Unable to validate duplicate Homescreen Widget", error);
      }
    }

    setLoader(true);
    const body = {
      name,
      sort_order: Number(sortOrder),
      tag: hasProtectedName ? null : aemTag,
      status,
      published_date: publishedDate,
      unpublished_date: neverEnding ? "null" : unpublishedDate,
    };
    fetch(edit ? `${endpoint}/${item.id}` : endpoint, {
      method: edit ? "PUT" : "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    })
      .then((response) => {
        if (!response.ok) throw new Error();
        setSuccessAlert(true);
      })
      .catch(() =>
        setErrorMessage(
          `Unable to ${edit ? "update" : "add"} Homescreen Widget.`,
        ),
      )
      .finally(() => setLoader(false));
  };

  const hasProtectedName = protectedWidgetName(name);
  const isProtectedWidget = edit && hasProtectedName;

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          breadcrumbItem={`${edit ? "Edit" : "Add"} Homescreen Widget`}
        />
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <Card>
                  <CardBody>
                    <Row>
                      <Col lg={4}>
                        <div className="mb-3">
                          <Label>Position</Label>
                          <input
                            type="number"
                            min="0"
                            className="form-control"
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                          />
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg={4}>
                        <div className="mb-3">
                          <Label>Name</Label>
                          <input
                            className="form-control"
                            value={name}
                            readOnly={isProtectedWidget}
                            onChange={(e) => setName(e.target.value)}
                          />
                        </div>
                      </Col>
                    </Row>
                    {!hasProtectedName && (
                      <Row>
                        <Col lg={4}>
                          <div className="mb-3">
                            <Label>AEM tag</Label>
                            <Select
                              value={aemTagOptions.find(
                                (option) => option.value === aemTag,
                              )}
                              onChange={(option) => setAemTag(option.value)}
                              options={aemTagOptions}
                            />
                          </div>
                        </Col>
                      </Row>
                    )}
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <Row>
                      <Col lg={4}>
                        <div className="mb-3">
                          <Label>Published Date</Label>
                          <input
                            type="datetime-local"
                            className="form-control"
                            value={publishedDate}
                            onChange={(e) => setPublishedDate(e.target.value)}
                          />
                        </div>
                      </Col>
                      <Col lg={4}>
                        <div className="mb-3">
                          <Label>Unpublished Date</Label>
                          <input
                            type="datetime-local"
                            className="form-control"
                            disabled={neverEnding}
                            value={unpublishedDate}
                            onChange={(e) => setUnpublishedDate(e.target.value)}
                          />
                        </div>
                      </Col>
                      <Col style={{ paddingTop: 35 }}>
                        <div className="form-check form-switch">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="neverEnding"
                            checked={neverEnding}
                            onChange={() => setNeverEnding(!neverEnding)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="neverEnding"
                          >
                            Never-ending
                          </label>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <Row>
                      <Col lg={4}>
                        <div className="mb-3">
                          <Label>Status</Label>
                          <Select
                            value={statusOptions.find(
                              (option) => option.value === status,
                            )}
                            onChange={(option) => setStatus(option.value)}
                            options={statusOptions}
                          />
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
                <div className="text-end">
                  <Button
                    color="danger"
                    onClick={() => props.history.push("/homescreen-widgets")}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="success"
                    className="ms-2"
                    disabled={loader}
                    onClick={submit}
                    style={{ minWidth: 70 }}
                  >
                    {loader ? (
                      <CircularProgress size={15} color="inherit" />
                    ) : (
                      "Save"
                    )}
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
        {successAlert && (
          <SweetAlert
            title="Success!"
            success
            confirmBtnBsStyle="success"
            onConfirm={() => props.history.push("/homescreen-widgets")}
          >
            Homescreen Widget {edit ? "updated" : "added"} successfully.
          </SweetAlert>
        )}
        {errorMessage && (
          <SweetAlert
            title="Warning!"
            warning
            confirmBtnBsStyle="warning"
            onConfirm={() => setErrorMessage("")}
          >
            {errorMessage}
          </SweetAlert>
        )}
      </Container>
    </div>
  );
};

export default HomescreenWidgetForm;
